import { memo, useCallback, useMemo, useRef } from 'react';
import equal from 'fast-deep-equal';
import { cn } from '@divlab/divanui';

import * as Cart from '@Queries/Cart';
import * as PageProductStore from '@Stores/PageProduct';
import { useRelatedProducts } from '@Queries/RelatedProducts';
import { useDeps } from '@Contexts/DI';
import { useMeta } from '@Queries/Meta';
import useNavigation from '@Navigation/useNavigation';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './BuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { CartModalInputProduct } from '@Types/Cart';

export interface BuyButtonProps extends ButtonProps {
  className?: string;
}

const BuyButton: FC<BuyButtonProps> = (props) => {
  const { className, ...restProps } = props;
  const page = PageProductStore.useData();
  const { t } = useTranslation();
  const parameterValues = PageProductStore.getParameterValues();
  const defaultParameterValues = useRef(parameterValues);
  const { logger } = useDeps();
  const { openPage } = useNavigation();
  const meta = useMeta();
  const { region } = meta.data;
  const addToCart = Cart.useAddToCart();
  const { data: relatedProducts } = useRelatedProducts(page.product.id);

  const isModifiedParameters = useMemo(() => {
    return !equal(defaultParameterValues.current, parameterValues);
  }, [parameterValues, defaultParameterValues]);

  const shopProduct = useMemo(
    () => ({
      isModular: page.isModular,
      shopProductId: page.product.id,
      parameterValues,
      isModifiedParameters,
    }),
    [page.isModular, page.product.id, parameterValues, isModifiedParameters],
  );

  const hasInCart = Cart.useHasInCart(shopProduct);

  const handleError = useCallback(
    (err: unknown) => {
      logger.log(err);
    },
    [logger],
  );

  const handleClick = useCallback(() => {
    if (hasInCart) {
      openPage({ url: `${region.url}/order/check` });
      return;
    }

    if (addToCart.isPending || addToCart.isError) {
      return;
    }

    const products: CartModalInputProduct[] = [shopProduct];

    const selectedRelatedProducts =
      relatedProducts?.products.filter((product) => product.quantity > 0) || [];

    selectedRelatedProducts.forEach((product) => {
      products.push({
        shopProductId: product.id,
        quantity: product.quantity,
        isModular: false,
      });
    });

    addToCart.mutate(
      {
        products,
        analyticsLabel: 'Карточка товара',
        options: { isRelated: false },
      },
      {
        onError: handleError,
      },
    );
    // ModalWindows.open('Cart', {
    //   products: [shopProduct],
    //   analyticsLabel: 'Карточка товара',
    // });
  }, [
    hasInCart,
    addToCart,
    shopProduct,
    relatedProducts?.products,
    handleError,
    openPage,
    region.url,
  ]);

  return hasInCart ? (
    <Button
      {...restProps}
      wide
      className={cn(styles.buyButton, styles.sizeBig, { [styles.hasInCart]: hasInCart }, className)}
      theme='transparent'
      data-testid='sidebar-buy-button'
      onClick={handleClick}
    >
      <div className={styles.iconCheck} />
      {t('ui.in-cart')}
    </Button>
  ) : (
    <Button
      {...restProps}
      wide
      className={cn(styles.buyButton, styles.sizeBig, className)}
      data-testid='sidebar-buy-button'
      waiting={addToCart.isPending}
      onClick={handleClick}
    >
      {t('ui.add-to-cart')}
    </Button>
  );
};

export default memo(BuyButton);
